import styles from "./ClusterModeDropdown.module.scss";
import { useCallback, useMemo } from "react";
import type { MenuProps } from "antd/es/menu";
import { ClustersIcon, DownChevronIcon, FlatViewIcon } from "./Icons";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { getThresholdsRange } from "../../../../../../redux/SingleDataset/selectors";
import { useSearchParams } from "react-router-dom";
import {
  AUTOMATIC_FLAT_RESPONSE_PARAMETER,
  FLAT_RESPONSE_PARAMETER,
  GRANULARITY_PARAMETER,
  PAGE_PARAMETER,
} from "../../../../../../helpers/constants/miscellaneous";
import { amplitudeTrack } from "../../../../../../helpers/utility/amplitude";
import { AMP_DATASET_EXPLORATION_EVENT__GRANULARITY_CHANGED } from "../../../../../../helpers/constants/amplitudeEvents";
import {
  AMP_DATASET_EXPLORATION_PROPERTY__GRANULARITY_LEVEL,
  AMP_DATASET_ID,
} from "../../../../../../helpers/constants/amplitudeProperties";
import { VLDropdown } from "../../../../../uikit/VLDropdown/VLDropdown";
import { Tooltip } from "antd";

const FLAT_VIEW = "Flat View";
const VISUAL_SIMILARITY = "Visual Similarity";

const getGranularityDisplay = (count: number) => {
  switch (count) {
    case 2:
      return ["Low", "High"];
    case 3:
      return ["Low", "Medium", "High"];
    case 5:
      return ["Lowest", "Low", "Medium", "High", "Highest"];
    default:
      return [];
  }
};

export const ClusterModeDropdown = ({
  datasetId,
  disabled,
}: {
  disabled: boolean;
  datasetId: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const thresholdsRange = useSelector(getThresholdsRange);
  const hasViewParams =
    searchParams.has(GRANULARITY_PARAMETER) ||
    searchParams.has(FLAT_RESPONSE_PARAMETER) ||
    searchParams.has(AUTOMATIC_FLAT_RESPONSE_PARAMETER);
  const isFlatMode =
    searchParams.get(FLAT_RESPONSE_PARAMETER) === "true" ||
    searchParams.get(AUTOMATIC_FLAT_RESPONSE_PARAMETER) === "true" ||
    (process.env.REACT_APP_DEFAULT_FLAT_VIEW === "true" && !hasViewParams);
  const granularity =
    searchParams.get(GRANULARITY_PARAMETER) ||
    String(thresholdsRange.maxThreshold);

  const handleGranularity = useCallback(
    (value: string) => {
      searchParams.delete(FLAT_RESPONSE_PARAMETER);
      searchParams.delete(AUTOMATIC_FLAT_RESPONSE_PARAMETER);
      searchParams.set(GRANULARITY_PARAMETER, value);
      searchParams.set(PAGE_PARAMETER, "1");
      setSearchParams(searchParams);

      amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__GRANULARITY_CHANGED, {
        [AMP_DATASET_ID]: datasetId,
        [AMP_DATASET_EXPLORATION_PROPERTY__GRANULARITY_LEVEL]:
          Number(value) + 1, //+1 to start from 1 rather than 0
      });
    },
    [datasetId, searchParams, setSearchParams],
  );
  const handleFlatView = useCallback(() => {
    searchParams.delete(GRANULARITY_PARAMETER);
    searchParams.set(PAGE_PARAMETER, "1");
    searchParams.set(FLAT_RESPONSE_PARAMETER, "true");
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);
  const granularities = getGranularityDisplay(thresholdsRange.count);
  const menuItems = useMemo<MenuProps["items"]>(() => {
    return [
      {
        key: FLAT_VIEW,
        label: (
          <div className={styles.label}>
            <FlatViewIcon /> {FLAT_VIEW}
          </div>
        ),
        onClick: handleFlatView,
      },
      {
        key: VISUAL_SIMILARITY,
        label: (
          <div className={styles.label}>
            <ClustersIcon /> {VISUAL_SIMILARITY}
          </div>
        ),
        children: [
          {
            key: "granularity",
            type: "group",
            label: "Granularity Level:",
            children: thresholdsRange.values.map((value) => ({
              key: value,
              label: granularities[Number(value)] || value + 1,
              onClick: () => handleGranularity(value),
            })),
          },
        ],
      },
    ];
  }, [
    granularities,
    handleFlatView,
    handleGranularity,
    thresholdsRange.values,
  ]);
  const activeKey = isFlatMode ? FLAT_VIEW : granularity;
  const tooltipText = isFlatMode
    ? FLAT_VIEW
    : `Similarity level: ${granularities[Number(granularity)] || granularity}`;
  return (
    <VLDropdown
      className={styles.dropdown}
      trigger={["click"]}
      menu={{
        selectable: true,
        items: menuItems,
        selectedKeys: [activeKey],
      }}
    >
      <Tooltip title={tooltipText}>
        <div
          className={classNames(styles.button, { [styles.disabled]: disabled })}
        >
          {isFlatMode ? <FlatViewIcon /> : <ClustersIcon />}
          <DownChevronIcon />
        </div>
      </Tooltip>
    </VLDropdown>
  );
};
